import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Phone, Mail } from "lucide-react";
import Seo from "../components/Seo";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";
import { fadeUp, SecondaryCTA } from "../components/landing/shared";

export default function NotFoundPage() {
    useEffect(() => {
        window.scrollTo(0, 0);
        // Ha a build fájlt lokálisan nyitják meg (VS Code Live Server, file://, localhost),
        // átirányítjuk az éles domainre — a lokális kontextusban a React Router routing nem működik.
        const host = window.location.hostname;
        const isLocal =
            window.location.protocol === "file:" ||
            host === "localhost" ||
            host === "0.0.0.0" ||
            /^127\./.test(host) ||
            /^192\.168\./.test(host);
        if (isLocal) {
            window.location.replace("https://stabilor.hu/");
        }
    }, []);

    return (
        <div
            className="bg-white min-h-screen flex flex-col"
            data-testid="notfound-page"
        >
            <Seo
                title="404 — Az oldal nem található"
                description="A keresett oldal nem található. Térjen vissza a Stabilor főoldalra."
                path="/404"
                noindex
            />
            <Navbar />

            <main className="flex-1 relative bg-[#F8FAFC] overflow-hidden flex items-center">
                {/* Decorative triangle from left */}
                <div
                    aria-hidden
                    className="absolute top-0 left-0 h-full w-[45%] pointer-events-none"
                >
                    <svg
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                        className="w-full h-full"
                    >
                        <polygon
                            points="0,0 0,100 100,100"
                            fill="#0D7A73"
                            fillOpacity="0.08"
                        />
                        <polygon
                            points="0,30 0,100 60,100"
                            fill="#0D7A73"
                            fillOpacity="0.15"
                        />
                    </svg>
                </div>
                {/* Decorative triangle from right */}
                <div
                    aria-hidden
                    className="absolute top-0 right-0 h-full w-[35%] pointer-events-none"
                >
                    <svg
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                        className="w-full h-full"
                    >
                        <polygon
                            points="100,0 100,100 0,0"
                            fill="#0D7A73"
                            fillOpacity="0.06"
                        />
                    </svg>
                </div>
                <div
                    aria-hidden
                    className="absolute top-32 left-0 w-24 h-1 bg-[#0D7A73] pointer-events-none"
                />
                <div
                    aria-hidden
                    className="absolute bottom-32 right-0 w-24 h-1 bg-[#0D7A73] pointer-events-none"
                />

                <div className="relative w-full max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 pt-32 sm:pt-40 pb-24 text-center">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        animate="show"
                    >
                        <div className="section-num">Hiba történt</div>
                    </motion.div>

                    <motion.h1
                        variants={fadeUp}
                        custom={1}
                        initial="hidden"
                        animate="show"
                        className="mt-6 font-display-alt font-extrabold text-[7rem] sm:text-[12rem] lg:text-[16rem] leading-[0.85] text-[#0A0A0A] tabular"
                        data-testid="notfound-code"
                    >
                        4
                        <span className="text-[#0D7A73]">0</span>
                        4
                    </motion.h1>

                    <motion.h2
                        variants={fadeUp}
                        custom={2}
                        initial="hidden"
                        animate="show"
                        className="mt-8 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-tight"
                    >
                        Ez az oldal nem található.
                    </motion.h2>

                    <motion.p
                        variants={fadeUp}
                        custom={3}
                        initial="hidden"
                        animate="show"
                        className="mt-6 text-slate-600 text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed"
                    >
                        A keresett oldal talán áthelyezésre került, vagy már nem elérhető.
                        Térjen vissza a főoldalra, vagy vegye fel velünk a kapcsolatot.
                    </motion.p>

                    <motion.div
                        variants={fadeUp}
                        custom={4}
                        initial="hidden"
                        animate="show"
                        className="mt-12 flex flex-wrap gap-4 justify-center"
                    >
                        <Link
                            to="/"
                            data-testid="notfound-home-btn"
                            className="group inline-flex items-center gap-3 px-8 py-4 font-display font-bold text-base sm:text-lg border-2 bg-[#0D7A73] text-white border-[#0D7A73] hover:bg-[#0A5E58] hover:border-[#0A5E58] transition-colors duration-300"
                        >
                            <ArrowLeft className="w-5 h-5" strokeWidth={2.5} />
                            Vissza a főoldalra
                        </Link>
                        <SecondaryCTA
                            testId="notfound-contact-btn"
                            href="/#kapcsolat"
                        >
                            Kapcsolatfelvétel
                        </SecondaryCTA>
                    </motion.div>

                    <motion.div
                        variants={fadeUp}
                        custom={5}
                        initial="hidden"
                        animate="show"
                        className="mt-16 pt-10 border-t border-black/10 flex flex-col sm:flex-row justify-center gap-8 text-slate-600"
                    >
                        <a
                            href="tel:+36706534187"
                            className="flex items-center gap-3 justify-center hover:text-[#0D7A73] transition-colors"
                            data-testid="notfound-phone"
                        >
                            <Phone className="w-5 h-5 text-[#0D7A73]" />
                            <span className="font-mono-label text-sm tabular">
                                +36 70 653 4187
                            </span>
                        </a>
                        <a
                            href="mailto:iroda@stabilor.hu"
                            className="flex items-center gap-3 justify-center hover:text-[#0D7A73] transition-colors"
                            data-testid="notfound-email"
                        >
                            <Mail className="w-5 h-5 text-[#0D7A73]" />
                            <span className="font-mono-label text-sm">
                                iroda@stabilor.hu
                            </span>
                        </a>
                    </motion.div>
                </div>
            </main>

            <Footer />
        </div>
    );
}
